// ============================================================================
// DRAWING MANAGER V3 - Main Component
// Enhanced drawing management with folders, viewer, markup, version compare
// Date: January 2026
// ============================================================================

'use client';

import React, { useState, useEffect, useCallback } from 'react';
import dynamic from 'next/dynamic';
import {
  FileText, Upload, Download, Eye, Edit2, Trash2,
  CheckCircle, XCircle, AlertCircle, Clock,
  Grid3x3, List, Search, Filter, Plus, X,
  ChevronDown, ChevronRight, FolderTree, Settings,
  Share2, Copy, MoreVertical, Tag, Layers,
  ZoomIn, ZoomOut, Maximize, RotateCw, Info, GitCompare,
  Building2
} from 'lucide-react';
import { authService } from '@/lib/authService';
import { useTheme } from '@/contexts/ThemeContext';
import DrawingFolderTree from './DrawingFolderTree';
import DrawingViewer from './DrawingViewer';
import DrawingVersionCompare from './DrawingVersionCompare';
import DrawingBatchOperations from './DrawingBatchOperations';
import DrawingTransmittalModal from './DrawingTransmittal';
import FieldDrawingViewer from '@/app/client-zone/modules/total-operations/components/smart-task/components/DrawingViewer';
import { resolveDrawingFileUrl } from './drawingFileUrl';
import type {
  Drawing, DrawingFolder, DrawingFilters, ViewMode,
  DrawingDiscipline, DrawingType, DrawingStatus, DrawingVersion,
  DRAWING_DISCIPLINES, DRAWING_TYPES, STATUS_CONFIG, FILE_TYPE_CONFIG
} from './types';

// Dynamic import for BIM Viewer (heavy Three.js component)
const BIMViewer = dynamic(() => import('@/components/shared/BIMViewer'), {
  ssr: false,
  loading: () => (
    <div className="flex items-center justify-center h-96 bg-gray-50 dark:bg-gray-800 rounded-lg">
      <div className="text-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Loading 3D Viewer...</p>
      </div>
    </div>
  )
});

// Export sub-components for direct use
export { default as DrawingViewer } from './DrawingViewer';
export { default as DrawingFolderTree } from './DrawingFolderTree';
export { default as DrawingVersionCompare } from './DrawingVersionCompare';
export { default as DrawingComments } from './DrawingComments';
export { default as DrawingMarkupLayer } from './DrawingMarkupLayer';
export { default as DrawingBatchOperations } from './DrawingBatchOperations';
export { default as DrawingTransmittal } from './DrawingTransmittal';
export { resolveDrawingFileUrl } from './drawingFileUrl';

// Re-export constants for use
import {
  DRAWING_DISCIPLINES as DISCIPLINES,
  DRAWING_TYPES as TYPES,
  STATUS_CONFIG as STATUSES,
  FILE_TYPE_CONFIG as FILE_TYPES,
  SHEET_SIZES
} from './types';
import { notify } from '@/lib/notifications';
import { invalidatePublishedDrawingsRegisterCache } from '@/lib/smartTask/publishedDrawingsRegisterCache';
import { useOperationsCloudStorage, type CloudFolder } from './useOperationsCloudStorage';

// ============================================================================
// PROPS & CONFIG
// ============================================================================

type ActiveTab = 'drawings' | 'bim';

interface DrawingManagerV3Props {
  workItemId?: string | null;
  mode?: 'manage' | 'select' | 'view-only';
  onSelect?: (drawing: Drawing) => void;
  onMultiSelect?: (drawings: Drawing[]) => void;
  showUpload?: boolean;
  showFolders?: boolean;
  showBIMViewer?: boolean;
  initialFolderId?: string | null;
  defaultStatusFilter?: 'all' | 'draft' | 'pending_approval' | 'approved' | 'published' | 'superseded';
  /** When true, only shows published drawings (enforced by API). Ideal for field workers/SmartTask. */
  publishedOnly?: boolean;
  /** Show approval workflow controls (submit, approve, reject, publish) */
  showApprovalWorkflow?: boolean;
  /** Callback when approval action is taken */
  onApprovalAction?: (drawingId: string, action: string) => void;
  /** Use cloud storage (Operations Drive) for folder management */
  useCloudStorage?: boolean;
  /** Show cloud storage stats in UI */
  showStorageStats?: boolean;
  /** Use the SmartTask field viewer for view-only/mobile-friendly viewing */
  viewerVariant?: 'manager' | 'field';
  /** Show record-keeping panels for issue/change/transmittal control */
  showRecordKeeping?: boolean;
  /** Optional title for the record-keeping section */
  recordKeepingTitle?: string;
  /** When embedded in WorkItemDetailModal: hide bulk email share (use Work Item Preview). */
  embeddedInWorkItemDetail?: boolean;
  /** Called after drawings list is refreshed from the API */
  onDrawingsLoaded?: (count: number) => void;
}

interface DrawingTransmittalRecord {
  id: string;
  transmittal_number: string;
  subject?: string | null;
  purpose?: string | null;
  drawings?: Array<Partial<Drawing>>;
  recipients?: Array<string | Record<string, any>>;
  status?: string | null;
  sent_by_name?: string | null;
  sent_at?: string | null;
  created_at: string;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const canPreviewAsImage = (fileType: string): boolean => {
  const type = fileType?.toLowerCase() || '';
  return ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'svg'].includes(type);
};

const parseJsonArray = <T,>(value: unknown): T[] => {
  if (Array.isArray(value)) {
    return value as T[];
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? (parsed as T[]) : [];
    } catch {
      return [];
    }
  }

  return [];
};

const formatDisplayDate = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatPersonName = (value?: string | null): string => {
  return value?.trim() || 'System';
};

// ============================================================================
// DRAWING CARD COMPONENT
// ============================================================================

interface DrawingCardProps {
  drawing: Drawing;
  viewMode: ViewMode;
  isSelected: boolean;
  onSelect: (drawing: Drawing, multi: boolean) => void;
  onView: (drawing: Drawing) => void;
  onEdit: (drawing: Drawing) => void;
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
  onPublish?: (id: string) => void;
  onSubmitForApproval?: (id: string) => void;
  onDelete?: (id: string) => void;
  isDark: boolean;
  isAdmin?: boolean;
  currentUserId?: number | null;
  showApprovalWorkflow?: boolean;
}

function DrawingCard({
  drawing, viewMode, isSelected, onSelect, onView, onEdit, onApprove, onReject, onPublish, onSubmitForApproval, onDelete, isDark, isAdmin = false, currentUserId, showApprovalWorkflow = true
}: DrawingCardProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  const isCreator = drawing.created_by === currentUserId;
  const canEdit = isCreator || isAdmin;
  const canSubmit = (isCreator || isAdmin) && drawing.status === 'draft';
  const canApprove = isAdmin && drawing.status === 'pending_approval';
  const canPublish = isAdmin && drawing.status === 'approved';

  const fileType = drawing.file_type?.toLowerCase() || '';
  const fileConfig = FILE_TYPES[fileType] || { icon: '📁', label: fileType.toUpperCase(), canPreview: false };
  const statusConfig = STATUSES[drawing.status] || STATUSES.draft;
  const disciplineConfig = DISCIPLINES.find(d => d.value === drawing.discipline);
  const canPreview = canPreviewAsImage(fileType);
  const fullUrl = resolveDrawingFileUrl(drawing.file_url);

  if (viewMode === 'list') {
    return (
      <div
        className={`group flex items-center gap-4 p-3 rounded-lg border transition-all cursor-pointer ${
          isSelected
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : isDark
              ? 'border-gray-700 hover:border-gray-600 hover:bg-gray-800'
              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
        }`}
        onClick={(e) => onSelect(drawing, e.ctrlKey || e.metaKey)}
        onDoubleClick={() => onView(drawing)}
      >
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => {
            e.stopPropagation();
            onSelect(drawing, true);
          }}
          className="w-4 h-4 rounded border-gray-300"
        />

        {/* Thumbnail */}
        <div className="w-12 h-12 flex-shrink-0 rounded bg-gray-100 dark:bg-gray-800 overflow-hidden flex items-center justify-center">
          {canPreview && !imageError ? (
            <img
              src={fullUrl}
              alt={drawing.drawing_name}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <span className="text-2xl">{fileConfig.icon}</span>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className={`font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {drawing.drawing_name}
            </h3>
            {drawing.is_critical && (
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            )}
          </div>
          <div className={`flex items-center gap-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {drawing.drawing_number && <span>{drawing.drawing_number}</span>}
            <span>Rev {drawing.revision}</span>
            {disciplineConfig && (
              <span className="flex items-center gap-1">
                <span>{disciplineConfig.icon}</span>
                {disciplineConfig.label}
              </span>
            )}
            <span>{formatFileSize(drawing.file_size)}</span>
            {drawing.work_item_id && (
              <span className="text-blue-500 dark:text-blue-400" title={`Linked job: ${drawing.work_item_id}`}>
                Job linked
              </span>
            )}
          </div>
        </div>

        {/* Status */}
        <span className={`px-2 py-1 text-xs rounded ${statusConfig.bgColor} ${statusConfig.color}`}>
          {statusConfig.icon} {statusConfig.label}
        </span>

        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); onView(drawing); }}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
            title="View"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(drawing); }}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
            title="Edit"
          >
            <Edit2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // Grid view
  return (
    <div
      className={`group rounded-lg border overflow-hidden transition-all cursor-pointer ${
        isSelected
          ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800'
          : isDark
            ? 'border-gray-700 hover:border-gray-600'
            : 'border-gray-200 hover:border-gray-300'
      } ${isDark ? 'bg-gray-800' : 'bg-white'}`}
      onClick={(e) => onSelect(drawing, e.ctrlKey || e.metaKey)}
      onDoubleClick={() => onView(drawing)}
    >
      {/* Thumbnail */}
      <div className="aspect-[4/3] bg-gray-100 dark:bg-gray-900 relative overflow-hidden">
        {canPreview && !imageError ? (
          <img
            src={fullUrl}
            alt={drawing.drawing_name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
            <span className="text-5xl mb-2">{fileConfig.icon}</span>
            <span className="text-sm">{fileConfig.label}</span>
          </div>
        )}

        {/* Selection checkbox */}
        <div className="absolute top-2 left-2">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onSelect(drawing, true);
            }}
            className="w-4 h-4 rounded border-gray-300 bg-white/80"
          />
        </div>

        {/* Critical badge */}
        {drawing.is_critical && (
          <div className="absolute top-2 right-2 px-2 py-1 bg-red-500 text-white text-xs rounded flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Critical
          </div>
        )}

        {/* Discipline badge */}
        {disciplineConfig && (
          <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 text-white text-xs rounded flex items-center gap-1">
            <span>{disciplineConfig.icon}</span>
            {disciplineConfig.label}
          </div>
        )}

        {/* Hover overlay with approval workflow */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-2 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); onView(drawing); }}
            className="p-2 bg-white/20 hover:bg-white/30 rounded-full"
            title="View"
          >
            <Eye className="w-5 h-5 text-white" />
          </button>
          {canEdit && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(drawing); }}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-full"
              title="Edit"
            >
              <Edit2 className="w-5 h-5 text-white" />
            </button>
          )}
          {/* Submit for approval (draft drawings, creator or admin) */}
          {showApprovalWorkflow && canSubmit && onSubmitForApproval && (
            <button
              onClick={(e) => { e.stopPropagation(); onSubmitForApproval(drawing.id); }}
              className="p-2 bg-amber-500/30 hover:bg-amber-500/50 rounded-full"
              title="Submit for Approval"
            >
              <Clock className="w-5 h-5 text-white" />
            </button>
          )}
          {/* Approve (pending drawings, admin only) */}
          {showApprovalWorkflow && canApprove && onApprove && (
            <button
              onClick={(e) => { e.stopPropagation(); onApprove(drawing.id); }}
              className="p-2 bg-green-500/30 hover:bg-green-500/50 rounded-full"
              title="Approve"
            >
              <CheckCircle className="w-5 h-5 text-white" />
            </button>
          )}
          {/* Publish (approved drawings, admin only) */}
          {showApprovalWorkflow && canPublish && onPublish && (
            <button
              onClick={(e) => { e.stopPropagation(); onPublish(drawing.id); }}
              className="p-2 bg-blue-500/30 hover:bg-blue-500/50 rounded-full"
              title="Publish"
            >
              <Share2 className="w-5 h-5 text-white" />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-3">
        <h3 className={`font-medium text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`} title={drawing.drawing_name}>
          {drawing.drawing_name}
        </h3>
        
        {drawing.drawing_number && (
          <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {drawing.drawing_number} • Rev {drawing.revision}
          </p>
        )}
        {drawing.work_item_id && (
          <p className={`text-[10px] mt-1 truncate ${isDark ? 'text-blue-300' : 'text-blue-700'}`} title={drawing.work_item_id}>
            Linked job: {drawing.work_item_id}
          </p>
        )}

        <div className="flex items-center justify-between mt-2">
          <span className={`px-2 py-0.5 text-xs rounded ${statusConfig.bgColor} ${statusConfig.color}`}>
            {statusConfig.icon} {statusConfig.label}
          </span>
          <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            v{drawing.version}
          </span>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// UPLOAD MODAL
// ============================================================================

async function readApiErrorMessage(response: Response, fallback: string): Promise<string> {
  const contentType = response.headers.get('content-type') ?? '';
  try {
    if (contentType.includes('application/json')) {
      const data = (await response.json()) as {
        error?: unknown;
        message?: unknown;
        errors?: Array<{ error?: unknown; filename?: unknown }>;
      };

      const nestedErrors =
        Array.isArray(data.errors) && data.errors.length > 0
          ? data.errors
              .map((e) => {
                const fn = typeof e.filename === 'string' ? e.filename : '';
                const err = typeof e.error === 'string' ? e.error : '';
                return [fn, err].filter(Boolean).join(': ');
              })
              .filter(Boolean)
              .join('; ')
          : '';

      const summary =
        (typeof data.error === 'string' && data.error ? data.error : '')
        || (typeof data.message === 'string' && data.message ? data.message : '');

      if (nestedErrors && summary && summary !== nestedErrors) {
        return `${summary} — ${nestedErrors}`;
      }
      if (nestedErrors) return nestedErrors;
      if (summary) return summary;
    } else {
      const text = await response.text();
      const trimmed = text.trim();
      if (trimmed) return trimmed.length > 500 ? `${trimmed.slice(0, 500)}…` : trimmed;
    }
  } catch {
    // ignore parse errors
  }
  return `${fallback} (HTTP ${response.status})`;
}

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  folderId: string | null;
  onUploadComplete: () => void;
  isDark: boolean;
}

function UploadModal({ isOpen, onClose, folderId, onUploadComplete, isDark }: UploadModalProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [metadata, setMetadata] = useState({
    discipline: '' as DrawingDiscipline | '',
    drawing_type: '' as DrawingType | '',
    revision: 'A',
    tags: [] as string[]
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setUploadError(null);
      setFiles(Array.from(event.target.files));
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setUploadError(null);

    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        setUploadError('Not signed in or session expired. Refresh the page and try again.');
        return;
      }

      for (const file of files) {
        setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));

        const formData = new FormData();
        formData.append('files', file);
        if (folderId) {
          formData.append('folder_id', folderId);
        }

        const uploadResponse = await fetch('/api/v1/drawings/upload', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (!uploadResponse.ok) {
          throw new Error(await readApiErrorMessage(uploadResponse, 'Upload failed'));
        }

        const uploadData = await uploadResponse.json();
        setUploadProgress(prev => ({ ...prev, [file.name]: 50 }));

        const fileUrl = uploadData.file_url || uploadData.uploadedFiles?.[0]?.file_url;
        if (!fileUrl) {
          throw new Error('Upload succeeded but no file URL was returned. Check server logs.');
        }

        // Create drawing record
        const recordResponse = await fetch('/api/v1/drawings', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            folder_id: folderId,
            drawing_name: file.name.replace(/\.[^/.]+$/, ''),
            file_url: fileUrl,
            file_type: file.name.split('.').pop()?.toLowerCase(),
            file_size: file.size,
            original_filename: file.name,
            revision: metadata.revision,
            discipline: metadata.discipline || null,
            drawing_type: metadata.drawing_type || null,
            tags: metadata.tags,
            status: 'draft'
          })
        });

        if (!recordResponse.ok) {
          throw new Error(await readApiErrorMessage(recordResponse, 'Could not create drawing record'));
        }

        setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
      }

      onUploadComplete();
      onClose();
      setFiles([]);
      setUploadProgress({});
      setUploadError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Upload failed';
      setUploadError(message);
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4">
      <div className={`w-full max-w-2xl rounded-xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Upload Drawings
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Drop zone */}
          <label className={`block border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDark
              ? 'border-gray-600 hover:border-gray-500 hover:bg-gray-700/50'
              : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
          }`}>
            <Upload className="w-10 h-10 mx-auto mb-3 text-gray-400" />
            <p className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
              Drop files here or click to browse
            </p>
            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              PDF, DWG, DXF, PNG, JPG, STEP, IGES supported
            </p>
            <input
              type="file"
              multiple
              accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png,.tiff,.stp,.step,.igs,.iges,.svg"
              onChange={handleFileSelect}
              className="hidden"
            />
          </label>

          {/* File list */}
          {files.length > 0 && (
            <div className={`border rounded-lg divide-y ${isDark ? 'border-gray-700 divide-gray-700' : 'border-gray-200 divide-gray-200'}`}>
              {files.map((file, i) => (
                <div key={i} className="flex items-center justify-between p-3">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{FILE_TYPES[file.name.split('.').pop()?.toLowerCase() || '']?.icon || '📄'}</span>
                    <div>
                      <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{file.name}</p>
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{formatFileSize(file.size)}</p>
                    </div>
                  </div>
                  {uploadProgress[file.name] !== undefined ? (
                    <div className="w-20">
                      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 transition-all"
                          style={{ width: `${uploadProgress[file.name]}%` }}
                        />
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setFiles(files.filter((_, idx) => idx !== i))}
                      className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Discipline
              </label>
              <select
                value={metadata.discipline}
                onChange={(e) => setMetadata({ ...metadata, discipline: e.target.value as DrawingDiscipline })}
                className={`w-full px-3 py-2 border rounded-lg ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              >
                <option value="">Select discipline...</option>
                {DISCIPLINES.map(d => (
                  <option key={d.value} value={d.value}>{d.icon} {d.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Drawing Type
              </label>
              <select
                value={metadata.drawing_type}
                onChange={(e) => setMetadata({ ...metadata, drawing_type: e.target.value as DrawingType })}
                className={`w-full px-3 py-2 border rounded-lg ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              >
                <option value="">Select type...</option>
                {TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Initial Revision
              </label>
              <input
                type="text"
                value={metadata.revision}
                onChange={(e) => setMetadata({ ...metadata, revision: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                placeholder="A"
              />
            </div>
          </div>
        </div>

        {uploadError && (
          <div
            className={`mx-4 mb-2 flex gap-2 rounded-lg border px-3 py-2 text-sm ${
              isDark
                ? 'border-red-800 bg-red-950/80 text-red-200'
                : 'border-red-200 bg-red-50 text-red-800'
            }`}
            role="alert"
          >
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{uploadError}</span>
          </div>
        )}

        <div className={`flex justify-end gap-3 p-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={onClose}
            disabled={uploading}
            className={`px-4 py-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={files.length === 0 || uploading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Upload {files.length} File{files.length !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function DrawingManagerV3({
  workItemId = null,
  mode = 'manage',
  onSelect,
  onMultiSelect,
  showUpload = true,
  showFolders = true,
  showBIMViewer = true,
  initialFolderId = null,
  defaultStatusFilter = 'all',
  publishedOnly = false,
  showApprovalWorkflow = true,
  onApprovalAction,
  useCloudStorage = true,
  showStorageStats = true,
  viewerVariant = 'manager',
  showRecordKeeping = true,
  recordKeepingTitle = 'Drawing control register',
  embeddedInWorkItemDetail = false,
  onDrawingsLoaded,
}: DrawingManagerV3Props) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.mode === 'dark';

  // Tab state
  const [activeTab, setActiveTab] = useState<ActiveTab>('drawings');

  // Cloud storage hook - provides operations drive access
  const cloudStorage = useOperationsCloudStorage();
  const refreshCloudDrive = cloudStorage.refreshDrive;
  const cloudFolders = cloudStorage.folders;

  // State
  const [drawings, setDrawings] = useState<Drawing[]>([]);
  const [folders, setFolders] = useState<DrawingFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(initialFolderId);
  const [selectedDrawings, setSelectedDrawings] = useState<Set<string>>(new Set());
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showFolderPanel, setShowFolderPanel] = useState(showFolders);
  
  // RBAC state
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [stats, setStats] = useState<{ total: number; draft: number; pending_approval: number; approved: number; published: number; superseded: number } | null>(null);

  // Viewer state
  const [viewerDrawing, setViewerDrawing] = useState<Drawing | null>(null);
  const [showViewer, setShowViewer] = useState(false);
  const [viewerMode, setViewerMode] = useState<'view' | 'markup' | 'comment'>('view');

  // Version compare state
  const [compareDrawing, setCompareDrawing] = useState<Drawing | null>(null);
  const [drawingVersions, setDrawingVersions] = useState<DrawingVersion[]>([]);
  const [showVersionCompare, setShowVersionCompare] = useState(false);
  const [recordVersions, setRecordVersions] = useState<DrawingVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [transmittals, setTransmittals] = useState<DrawingTransmittalRecord[]>([]);
  const [transmittalsLoading, setTransmittalsLoading] = useState(false);

  // Batch & transmittal state
  const [showBatchOperations, setShowBatchOperations] = useState(true);
  const [showTransmittal, setShowTransmittal] = useState(false);

  // Filters
  const [filters, setFilters] = useState<DrawingFilters>({
    search: '',
    status: defaultStatusFilter,
    discipline: 'all',
    type: 'all',
    fileType: 'all',
    folderId: null,
    dateRange: { start: null, end: null },
    tags: []
  });
  const [showFilters, setShowFilters] = useState(false);

  const fetchDrawings = useCallback(async () => {
    try {
      setLoading(true);
      const token = authService.getTokens()?.access_token;
      const params = new URLSearchParams();
      
      if (selectedFolderId) params.append('folder_id', selectedFolderId);
      if (workItemId) params.append('work_item_id', workItemId);
      
      // RBAC: Use published_only for view-only mode OR when publishedOnly prop is true
      if (mode === 'view-only' || publishedOnly) {
        params.append('published_only', 'true');
      } else if (filters.status !== 'all') {
        // Server-side status filter (never send with published_only — that implied status=published)
        params.append('status', filters.status);
      }

      // Request stats for admin users
      params.append('include_stats', 'true');

      const response = await fetch(`/api/v1/drawings?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setDrawings(data.drawings || []);
        onDrawingsLoaded?.((data.drawings || []).length);
        setIsAdmin(data.isAdmin || false);
        setCurrentUserId(data.userId || null);
        if (data.stats) {
          setStats(data.stats);
        }
      }
    } catch (error) {
      console.error('Error fetching drawings:', error);
    } finally {
      setLoading(false);
    }
  }, [filters.status, mode, publishedOnly, selectedFolderId, workItemId, onDrawingsLoaded]);

  const fetchFolders = useCallback(async () => {
    // If using cloud storage, folders are managed by the hook
    if (useCloudStorage) {
      await refreshCloudDrive();
      return;
    }

    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch('/api/v1/drawings/folders', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setFolders(data.folders || []);
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  }, [refreshCloudDrive, useCloudStorage]);

  const fetchTransmittals = useCallback(async () => {
    try {
      setTransmittalsLoading(true);
      const token = authService.getTokens()?.access_token;
      const params = new URLSearchParams();
      if (workItemId) params.append('work_item_id', workItemId);
      params.append('limit', '20');

      const response = await fetch(`/api/v1/drawings/transmittals?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      const normalized = (data.transmittals || []).map((item: any) => ({
        ...item,
        drawings: parseJsonArray<Partial<Drawing>>(item.drawings),
        recipients: parseJsonArray<string | Record<string, any>>(item.recipients)
      }));
      setTransmittals(normalized);
    } catch (error) {
      console.error('Error fetching transmittals:', error);
    } finally {
      setTransmittalsLoading(false);
    }
  }, [workItemId]);

  // Fetch data
  useEffect(() => {
    fetchDrawings();
    if (!publishedOnly) {
      fetchFolders();
    }
  }, [fetchDrawings, fetchFolders, publishedOnly]);

  useEffect(() => {
    if (showRecordKeeping) {
      fetchTransmittals();
    }
  }, [fetchTransmittals, showRecordKeeping]);

  // Approval workflow actions
  const handleApprovalAction = async (drawingId: string, action: string, comments?: string) => {
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch('/api/v1/drawings', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id: drawingId, action, comments })
      });

      const data = await response.json();
      if (data.success) {
        notify.success(`Drawing ${action.replace(/_/g, ' ')} successful`);
        if (['publish', 'unpublish', 'approve', 'reject', 'submit_for_approval'].includes(action)) {
          invalidatePublishedDrawingsRegisterCache(workItemId);
        }
        if (data.drawing) {
          setViewerDrawing((v) =>
            v && String(v.id) === String(drawingId) ? { ...v, ...(data.drawing as Drawing) } : v,
          );
        }
        if (useCloudStorage) {
          await refreshCloudDrive();
        }
        await fetchDrawings();
        onApprovalAction?.(drawingId, action);
      } else {
        notify.error(data.error || `Failed to ${action}`);
      }
    } catch (error) {
      console.error('Approval action error:', error);
      notify.error('Failed to perform action');
    }
  };

  // Use cloud storage folders when enabled
  useEffect(() => {
    if (useCloudStorage && cloudFolders.length > 0) {
      // Convert cloud folders to DrawingFolder format
      const drawingFolders: DrawingFolder[] = cloudFolders.map(cf => ({
        id: cf.id,
        name: cf.name,
        parent_id: cf.parent_id,
        path: cf.path,
        client_id: 0, // Not needed for display
        drawing_count: cf.file_count,
        children: cf.children?.map(child => ({
          id: child.id,
          name: child.name,
          parent_id: child.parent_id,
          path: child.path,
          client_id: 0,
          drawing_count: child.file_count,
          children: []
        })) || []
      }));
      setFolders(drawingFolders);
    }
  }, [cloudFolders, useCloudStorage]);

  // Folder actions
  const handleCreateFolder = async (parentId: string | null, name: string) => {
    // Use cloud storage if enabled
    if (useCloudStorage) {
      await cloudStorage.createFolder(name, parentId);
      return;
    }

    try {
      const token = authService.getTokens()?.access_token;
      await fetch('/api/v1/drawings/folders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ parent_folder_id: parentId, name })
      });
      fetchFolders();
    } catch (error) {
      console.error('Error creating folder:', error);
    }
  };

  const handleRenameFolder = async (folderId: string, name: string) => {
    try {
      const token = authService.getTokens()?.access_token;
      await fetch(`/api/v1/drawings/folders/${folderId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name })
      });
      fetchFolders();
    } catch (error) {
      console.error('Error renaming folder:', error);
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    try {
      const token = authService.getTokens()?.access_token;
      await fetch(`/api/v1/drawings/folders/${folderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (selectedFolderId === folderId) setSelectedFolderId(null);
      fetchFolders();
    } catch (error) {
      console.error('Error deleting folder:', error);
    }
  };

  // Selection
  const handleSelectDrawing = (drawing: Drawing, multi: boolean) => {
    if (mode === 'select' && onSelect) {
      onSelect(drawing);
      return;
    }

    setSelectedDrawings(prev => {
      const next = new Set(prev);
      if (multi) {
        if (next.has(drawing.id)) {
          next.delete(drawing.id);
        } else {
          next.add(drawing.id);
        }
      } else {
        next.clear();
        next.add(drawing.id);
      }
      return next;
    });
  };

  // Viewer handlers
  const handleOpenViewer = (drawing: Drawing, mode: 'view' | 'markup' | 'comment' = 'view') => {
    setViewerDrawing(drawing);
    setViewerMode(mode);
    setShowViewer(true);
  };

  const handleOpenVersionCompare = async (drawing: Drawing) => {
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch(`/api/v1/drawings/${drawing.id}/versions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDrawingVersions(data.versions || []);
        setCompareDrawing(drawing);
        setShowVersionCompare(true);
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
    }
  };

  const selectedDrawingsList = drawings.filter(d => selectedDrawings.has(d.id));

  const fetchRecordVersions = useCallback(async (drawingId: string) => {
    try {
      setVersionsLoading(true);
      const token = authService.getTokens()?.access_token;
      const response = await fetch(`/api/v1/drawings/${drawingId}/versions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        setRecordVersions([]);
        return;
      }

      const data = await response.json();
      setRecordVersions(data.versions || []);
    } catch (error) {
      console.error('Error fetching record versions:', error);
      setRecordVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  }, []);

  // Batch operation handlers

  const handleBulkMoveToFolder = async (ids: string[], folderId: string | null) => {
    const token = authService.getTokens()?.access_token;
    await fetch('/api/v1/drawings/batch/move', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ drawing_ids: ids, folder_id: folderId })
    });
    fetchDrawings();
    setSelectedDrawings(new Set());
  };

  const handleBulkDelete = async (ids: string[]) => {
    const token = authService.getTokens()?.access_token;
    await fetch('/api/v1/drawings/batch/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ drawing_ids: ids })
    });
    invalidatePublishedDrawingsRegisterCache(workItemId);
    fetchDrawings();
    setSelectedDrawings(new Set());
  };

  const handleBulkStatusChange = async (ids: string[], status: string) => {
    const token = authService.getTokens()?.access_token;
    await fetch('/api/v1/drawings/batch/status', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ drawing_ids: ids, status })
    });
    invalidatePublishedDrawingsRegisterCache(workItemId);
    fetchDrawings();
    setSelectedDrawings(new Set());
  };

  const handleBulkTagAdd = async (ids: string[], tags: string[]) => {
    const token = authService.getTokens()?.access_token;
    await fetch('/api/v1/drawings/batch/tags', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ drawing_ids: ids, tags })
    });
    fetchDrawings();
  };

  const handleBulkDownload = async (ids: string[]) => {
    const token = authService.getTokens()?.access_token;
    const response = await fetch('/api/v1/drawings/batch/download', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ drawing_ids: ids })
    });
    if (response.ok) {
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'drawings.zip';
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleBulkShare = async (ids: string[], recipients: string[]) => {
    const token = authService.getTokens()?.access_token;
    await fetch('/api/v1/drawings/batch/share', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ drawing_ids: ids, recipients })
    });
  };

  const handleBulkLinkJob = async (ids: string[], targetWorkItemId: string) => {
    const wid = targetWorkItemId.trim();
    if (!wid) {
      notify.error('Enter a job (work item) ID');
      return;
    }
    const token = authService.getTokens()?.access_token;
    let failures = 0;
    for (const id of ids) {
      const res = await fetch(`/api/v1/work-items/${encodeURIComponent(wid)}/drawings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ drawingId: id }),
      });
      if (!res.ok) failures += 1;
    }
    if (failures === 0) {
      notify.success(ids.length === 1 ? 'Drawing linked to job' : `Linked ${ids.length} drawings to job`);
    } else {
      notify.error(`${failures} of ${ids.length} could not be linked (check job ID and permissions)`);
    }
    invalidatePublishedDrawingsRegisterCache(workItemId);
    fetchDrawings();
    setSelectedDrawings(new Set());
  };

  const handleBulkPrint = async (ids: string[]) => {
    console.log('Print drawings:', ids);
    // TODO: Implement print queue
  };

  const handleCreateTransmittal = (ids: string[]) => {
    setShowTransmittal(true);
  };

  const handleSendTransmittal = async (transmittal: any) => {
    const token = authService.getTokens()?.access_token;
    const recipients = [transmittal.to_email, ...(transmittal.copies_to || [])]
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      .map((value) => value.trim());

    const response = await fetch('/api/v1/drawings/transmittals', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({
        work_item_id: workItemId || null,
        subject: transmittal.subject,
        description: transmittal.notes || null,
        purpose: transmittal.reason || 'for_information',
        drawings: selectedDrawingsList.map((drawing) => drawing.id),
        recipients,
        remarks: transmittal.notes || null,
        send_email: recipients.length > 0,
      })
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({ error: 'Failed to create transmittal' }));
      notify.error(data.error || 'Failed to create transmittal');
      return;
    }

    setShowTransmittal(false);
    setSelectedDrawings(new Set());
    fetchTransmittals();
    notify.success('Drawing transmittal recorded successfully');
  };

  // Filter drawings
  const filteredDrawings = drawings.filter(d => {
    if (filters.search && !d.drawing_name.toLowerCase().includes(filters.search.toLowerCase()) &&
        !d.drawing_number?.toLowerCase().includes(filters.search.toLowerCase())) return false;
    // Status is applied in fetchDrawings (API) — do not double-filter or lists empty when folder + chip disagree
    if (filters.discipline !== 'all' && d.discipline !== filters.discipline) return false;
    if (filters.type !== 'all' && d.drawing_type !== filters.type) return false;
    if (filters.fileType !== 'all' && d.file_type !== filters.fileType) return false;
    return true;
  });

  const recordFocusDrawing = viewerDrawing || selectedDrawingsList[0] || filteredDrawings[0] || null;

  useEffect(() => {
    if (recordFocusDrawing?.id && showRecordKeeping) {
      fetchRecordVersions(recordFocusDrawing.id);
    } else {
      setRecordVersions([]);
    }
  }, [fetchRecordVersions, recordFocusDrawing?.id, showRecordKeeping]);

  // Breadcrumb
  const getBreadcrumb = () => {
    if (!selectedFolderId) return [{ id: null, name: 'All Drawings' }];
    // Would traverse folder tree to build breadcrumb
    const folder = folders.find(f => f.id === selectedFolderId);
    return [
      { id: null, name: 'All Drawings' },
      folder ? { id: folder.id, name: folder.name } : null
    ].filter(Boolean);
  };

  const fieldViewerDrawings = filteredDrawings.map((drawing) => ({
    ...drawing,
    url: resolveDrawingFileUrl(drawing.file_url),
    title: drawing.drawing_name,
    mimeType: drawing.file_type === 'pdf'
      ? 'application/pdf'
      : canPreviewAsImage(drawing.file_type)
        ? `image/${drawing.file_type === 'jpg' ? 'jpeg' : drawing.file_type}`
        : undefined,
  }));

  const activePublishedCount = drawings.filter((drawing) => drawing.status === 'published' || drawing.is_published).length;
  const pendingApprovalCount = drawings.filter((drawing) => drawing.status === 'pending_approval').length;
  const totalIssuedCount = transmittals.filter((item) => item.status === 'sent' || item.status === 'acknowledged').length;

  if (loading && drawings.length === 0) {
    return (
      <div className={`h-full flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>Loading drawings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full flex ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className={`p-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          {embeddedInWorkItemDetail && (
            <p className={`mb-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Work item status: use the <strong className={isDark ? 'text-gray-200' : 'text-gray-800'}>Details</strong> sidebar.
              For customer-ready PDFs and email, use <strong className={isDark ? 'text-gray-200' : 'text-gray-800'}>Overview → Formal register</strong> in this tab.
            </p>
          )}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              {/* Tab switcher */}
              <div className={`flex rounded-lg border ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-300 bg-gray-100'}`}>
                <button
                  onClick={() => setActiveTab('drawings')}
                  className={`px-4 py-2 flex items-center gap-2 rounded-l-lg transition-colors ${
                    activeTab === 'drawings'
                      ? 'bg-blue-600 text-white'
                      : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <FileText className="w-4 h-4" />
                  2D Drawings
                </button>
                {showBIMViewer && (
                  <button
                    onClick={() => setActiveTab('bim')}
                    className={`px-4 py-2 flex items-center gap-2 rounded-r-lg transition-colors ${
                      activeTab === 'bim'
                        ? 'bg-blue-600 text-white'
                        : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Building2 className="w-4 h-4" />
                    3D BIM
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Folder toggle - only show for drawings tab */}
              {activeTab === 'drawings' && !showFolderPanel && (
                <button
                  onClick={() => setShowFolderPanel(true)}
                  className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
                  title="Show folders"
                >
                  <FolderTree className="w-5 h-5" />
                </button>
              )}
              
              {/* View toggles - only show for drawings tab */}
              {activeTab === 'drawings' && (
                <div className={`flex rounded-lg border ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30' : ''}`}
                  >
                    <Grid3x3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 ${viewMode === 'list' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30' : ''}`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              )}

              {activeTab === 'drawings' && showUpload && mode !== 'view-only' && (
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  Upload
                </button>
              )}
            </div>
          </div>

          {/* Search and filters - only show for drawings tab */}
          {activeTab === 'drawings' && (
            <div className="flex items-center gap-3">
              <div className="flex-1 relative">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <input
                  type="text"
                  placeholder="Search drawings..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg ${
                    isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-3 py-2 border rounded-lg flex items-center gap-2 ${
                  showFilters ? 'bg-blue-50 border-blue-200 text-blue-600' : ''
                } ${isDark ? 'border-gray-700' : 'border-gray-300'}`}
              >
                <Filter className="w-4 h-4" />
                Filters
                <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>
            </div>
          )}

          {/* Admin Stats Bar - shows drawing counts by status */}
          {activeTab === 'drawings' && isAdmin && stats && (
            <div className={`flex items-center gap-4 mt-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              <span className="font-medium">Stats:</span>
              <button
                onClick={() => setFilters({ ...filters, status: 'all' })}
                className={`flex items-center gap-1 px-2 py-1 rounded ${filters.status === 'all' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
              >
                📊 Total: {stats.total}
              </button>
              <button
                onClick={() => setFilters({ ...filters, status: 'draft' })}
                className={`flex items-center gap-1 px-2 py-1 rounded ${filters.status === 'draft' ? 'bg-gray-200 text-gray-700 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
              >
                📝 Draft: {stats.draft}
              </button>
              <button
                onClick={() => setFilters({ ...filters, status: 'pending_approval' })}
                className={`flex items-center gap-1 px-2 py-1 rounded ${filters.status === 'pending_approval' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
              >
                ⏳ Pending: {stats.pending_approval}
              </button>
              <button
                onClick={() => setFilters({ ...filters, status: 'approved' })}
                className={`flex items-center gap-1 px-2 py-1 rounded ${filters.status === 'approved' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
              >
                ✓ Approved: {stats.approved}
              </button>
              <button
                onClick={() => setFilters({ ...filters, status: 'published' })}
                className={`flex items-center gap-1 px-2 py-1 rounded ${filters.status === 'published' ? 'bg-green-100 text-green-600 dark:bg-green-900/30' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
              >
                🌐 Published: {stats.published}
              </button>
              {stats.superseded > 0 && (
                <button
                  onClick={() => setFilters({ ...filters, status: 'superseded' })}
                  className={`flex items-center gap-1 px-2 py-1 rounded ${filters.status === 'superseded' ? 'bg-gray-200 text-gray-600 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                >
                  🔄 Superseded: {stats.superseded}
                </button>
              )}
            </div>
          )}

          {/* Cloud Storage Stats Bar - shows storage usage */}
          {activeTab === 'drawings' && useCloudStorage && showStorageStats && cloudStorage.stats && (
            <div className={`flex items-center gap-4 mt-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              <span className="font-medium flex items-center gap-1">
                ☁️ Cloud Storage:
              </span>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all ${
                      cloudStorage.stats.usage_percent > 90 ? 'bg-red-500' :
                      cloudStorage.stats.usage_percent > 75 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(cloudStorage.stats.usage_percent, 100)}%` }}
                  />
                </div>
                <span>
                  {(cloudStorage.stats.total_used / 1024 / 1024).toFixed(1)} MB / 
                  {(cloudStorage.stats.quota_bytes / 1024 / 1024 / 1024).toFixed(1)} GB
                </span>
                <span className="text-xs">({cloudStorage.stats.usage_percent}%)</span>
              </div>
              <span className="text-xs">
                📄 {cloudStorage.stats.total_files} files
              </span>
              {cloudStorage.drive && (
                <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  {cloudStorage.drive.name}
                </span>
              )}
            </div>
          )}

          {/* Filter row */}
          {activeTab === 'drawings' && showFilters && (
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              {/* Status filter - only show for admins, non-admins see published only */}
              {isAdmin && (
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value as any })}
                  className={`px-3 py-1.5 border rounded-lg text-sm ${isDark ? 'bg-gray-800 border-gray-700' : 'border-gray-300'}`}
                >
                  <option value="all">All Status</option>
                  {Object.entries(STATUSES).map(([value, config]) => (
                    <option key={value} value={value}>{config.icon} {config.label}</option>
                  ))}
                </select>
              )}
              <select
                value={filters.discipline}
                onChange={(e) => setFilters({ ...filters, discipline: e.target.value as any })}
                className={`px-3 py-1.5 border rounded-lg text-sm ${isDark ? 'bg-gray-800 border-gray-700' : 'border-gray-300'}`}
              >
                <option value="all">All Disciplines</option>
                {DISCIPLINES.map(d => (
                  <option key={d.value} value={d.value}>{d.icon} {d.label}</option>
                ))}
              </select>
              <select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value as any })}
                className={`px-3 py-1.5 border rounded-lg text-sm ${isDark ? 'bg-gray-800 border-gray-700' : 'border-gray-300'}`}
              >
                <option value="all">All Types</option>
                {TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Selection bar - only show for drawings tab */}
        {activeTab === 'drawings' && selectedDrawings.size > 0 && (
          <div className={`px-4 py-2 flex items-center justify-between ${isDark ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
            <span className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
              {selectedDrawings.size} drawing{selectedDrawings.size !== 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleBulkDownload(Array.from(selectedDrawings))}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
              >
                <Download className="w-4 h-4" /> Download
              </button>
              {isAdmin && (
                <button
                  onClick={() => handleBulkStatusChange(Array.from(selectedDrawings), 'approved')}
                  className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1"
                >
                  <CheckCircle className="w-4 h-4" /> Approve
                </button>
              )}
              <button
                onClick={() => handleCreateTransmittal(Array.from(selectedDrawings))}
                className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center gap-1"
              >
                <FileText className="w-4 h-4" /> Issue Register
              </button>
              <button
                onClick={() => setSelectedDrawings(new Set())}
                className={`px-3 py-1 text-sm rounded ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Main content area */}
        {activeTab === 'drawings' ? (
          /* Drawings grid/list */
          <div className="flex-1 overflow-auto p-4">
            {filteredDrawings.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <FileText className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-700' : 'text-gray-300'}`} />
                  <h3 className={`text-lg font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>No drawings found</h3>
                  <p className={`mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {filters.search ? 'Try adjusting your search or filters' : 'Upload your first drawing to get started'}
                  </p>
                  {showUpload && mode !== 'view-only' && !filters.search && (
                    <button
                      onClick={() => setShowUploadModal(true)}
                      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto"
                    >
                      <Upload className="w-4 h-4" />
                      Upload Drawings
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className={
                  viewMode === 'grid'
                    ? 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4'
                    : 'space-y-2'
                }>
                  {filteredDrawings.map(drawing => (
                    <DrawingCard
                      key={drawing.id}
                      drawing={drawing}
                      viewMode={viewMode}
                      isSelected={selectedDrawings.has(drawing.id)}
                      onSelect={handleSelectDrawing}
                      onView={() => handleOpenViewer(drawing)}
                      onEdit={() => handleOpenViewer(drawing, 'markup')}
                      onSubmitForApproval={(id) => handleApprovalAction(id, 'submit_for_approval')}
                      onApprove={(id) => handleApprovalAction(id, 'approve')}
                      onReject={(id) => handleApprovalAction(id, 'reject')}
                      onPublish={(id) => handleApprovalAction(id, 'publish')}
                      isDark={isDark}
                      isAdmin={isAdmin}
                      currentUserId={currentUserId}
                      showApprovalWorkflow={showApprovalWorkflow && mode !== 'view-only'}
                    />
                  ))}
                </div>

                {showRecordKeeping && (
                  <div className={`rounded-2xl border p-4 md:p-5 ${isDark ? 'border-gray-700 bg-gray-800/70' : 'border-gray-200 bg-white'}`}>
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{recordKeepingTitle}</h3>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Track issued drawings, revision history, and distribution records for manufacturing control.
                          </p>
                        </div>
                        {recordFocusDrawing && (
                          <button
                            type="button"
                            onClick={() => handleOpenVersionCompare(recordFocusDrawing)}
                            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                          >
                            <GitCompare className="h-4 w-4" />
                            Compare versions for {recordFocusDrawing.drawing_number || recordFocusDrawing.drawing_name}
                          </button>
                        )}
                      </div>

                      <div className="grid gap-3 md:grid-cols-3">
                        <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
                          <p className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Published drawings</p>
                          <p className={`mt-2 text-2xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{activePublishedCount}</p>
                          <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Current controlled issue available to field teams.</p>
                        </div>
                        <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
                          <p className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Pending approvals</p>
                          <p className={`mt-2 text-2xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{pendingApprovalCount}</p>
                          <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Drawings waiting for engineering approval before issue.</p>
                        </div>
                        <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
                          <p className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Issued transmittals</p>
                          <p className={`mt-2 text-2xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{totalIssuedCount}</p>
                          <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Distribution log for drawing issue and record copies.</p>
                        </div>
                      </div>

                      <div className="grid gap-4 xl:grid-cols-[1.6fr_1fr]">
                        <div className={`overflow-hidden rounded-xl border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                          <div className={`flex items-center justify-between border-b px-4 py-3 ${isDark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-gray-50'}`}>
                            <div>
                              <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Drawing register</h4>
                              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Controlled list of drawings, revisions, and latest issue status.</p>
                            </div>
                            <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{filteredDrawings.length} entries</span>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead className={isDark ? 'bg-gray-900/40 text-gray-400' : 'bg-gray-50 text-gray-500'}>
                                <tr>
                                  <th className="px-4 py-3 text-left font-medium">Drawing</th>
                                  <th className="px-4 py-3 text-left font-medium">Revision</th>
                                  <th className="px-4 py-3 text-left font-medium">Status</th>
                                  <th className="px-4 py-3 text-left font-medium">Issued</th>
                                  <th className="px-4 py-3 text-left font-medium">Owner</th>
                                  <th className="px-4 py-3 text-left font-medium">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredDrawings.map((drawing) => {
                                  const statusConfig = STATUSES[drawing.status] || STATUSES.draft;
                                  const issuedOn = drawing.published_at || drawing.updated_at || drawing.created_at;
                                  const isFocused = recordFocusDrawing?.id === drawing.id;

                                  return (
                                    <tr
                                      key={`register-${drawing.id}`}
                                      className={`border-t ${isDark ? 'border-gray-700 text-gray-200' : 'border-gray-200 text-gray-700'} ${isFocused ? (isDark ? 'bg-blue-900/10' : 'bg-blue-50/60') : ''}`}
                                    >
                                      <td className="px-4 py-3">
                                        <div className="min-w-[15rem]">
                                          <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{drawing.drawing_name}</p>
                                          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{drawing.drawing_number || 'Unnumbered drawing'}</p>
                                        </div>
                                      </td>
                                      <td className="px-4 py-3">Rev {drawing.revision} / v{drawing.version}</td>
                                      <td className="px-4 py-3">
                                        <span className={`inline-flex rounded-full px-2 py-1 text-xs ${statusConfig.bgColor} ${statusConfig.color}`}>
                                          {statusConfig.icon} {statusConfig.label}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3 text-xs">{formatDisplayDate(issuedOn)}</td>
                                      <td className="px-4 py-3 text-xs">{drawing.created_by || '—'}</td>
                                      <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                          <button
                                            type="button"
                                            onClick={() => handleOpenViewer(drawing)}
                                            className="rounded-md border px-2 py-1 text-xs hover:bg-white/5"
                                          >
                                            View
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => handleOpenVersionCompare(drawing)}
                                            className="rounded-md border px-2 py-1 text-xs hover:bg-white/5"
                                          >
                                            History
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div className={`overflow-hidden rounded-xl border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                            <div className={`border-b px-4 py-3 ${isDark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-gray-50'}`}>
                              <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Change register</h4>
                              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {recordFocusDrawing ? `Revision trail for ${recordFocusDrawing.drawing_number || recordFocusDrawing.drawing_name}` : 'Select a drawing to inspect revision history.'}
                              </p>
                            </div>
                            <div className="max-h-[18rem] overflow-y-auto p-4">
                              {versionsLoading ? (
                                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading revision history…</p>
                              ) : recordVersions.length > 0 ? (
                                <div className="space-y-3">
                                  {recordVersions.map((version) => (
                                    <div key={version.id} className={`rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50'}`}>
                                      <div className="flex items-center justify-between gap-3">
                                        <div>
                                          <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Rev {version.revision} · v{version.version}</p>
                                          <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{version.change_description || version.changes_summary || 'No change description supplied.'}</p>
                                        </div>
                                        <span className={`text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{formatDisplayDate(version.created_at)}</span>
                                      </div>
                                      <div className={`mt-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                        Issued by {formatPersonName(version.uploaded_by_name || version.created_by_name)}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No revision history available yet.</p>
                              )}
                            </div>
                          </div>

                          <div className={`overflow-hidden rounded-xl border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                            <div className={`border-b px-4 py-3 ${isDark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-gray-50'}`}>
                              <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Issue / transmittal register</h4>
                              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Recorded distribution of drawing issues and controlled copies.</p>
                            </div>
                            <div className="max-h-[18rem] overflow-y-auto p-4">
                              {transmittalsLoading ? (
                                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading issue records…</p>
                              ) : transmittals.length > 0 ? (
                                <div className="space-y-3">
                                  {transmittals.map((item) => (
                                    <div key={item.id} className={`rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50'}`}>
                                      <div className="flex items-start justify-between gap-3">
                                        <div>
                                          <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.transmittal_number}</p>
                                          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{item.subject || 'Drawing transmittal'}</p>
                                        </div>
                                        <span className={`rounded-full px-2 py-1 text-[11px] ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700'}`}>{item.status || 'draft'}</span>
                                      </div>
                                      <div className={`mt-2 grid gap-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        <p>Purpose: {(item.purpose || 'for_information').replace(/_/g, ' ')}</p>
                                        <p>Drawings issued: {item.drawings?.length || 0}</p>
                                        <p>Recipients: {item.recipients?.length || 0}</p>
                                        <p>Issued by: {formatPersonName(item.sent_by_name)}</p>
                                        <p>Issued on: {formatDisplayDate(item.sent_at || item.created_at)}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No issue records have been created yet.</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* BIM 3D Viewer */
          <div className="flex-1 overflow-hidden" style={{ minHeight: '600px' }}>
            <BIMViewer
              buildingName="Project Building"
              showControls={true}
              height="calc(100vh - 200px)"
            />
          </div>
        )}
      </div>

      {/* Folder panel - RIGHT SIDE - only show for drawings tab */}
      {activeTab === 'drawings' && showFolderPanel && (
        <div className={`w-64 flex-shrink-0 border-l ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <DrawingFolderTree
            folders={folders}
            selectedFolderId={selectedFolderId}
            onSelectFolder={setSelectedFolderId}
            onCreateFolder={handleCreateFolder}
            onRenameFolder={handleRenameFolder}
            onDeleteFolder={handleDeleteFolder}
            onMoveFolder={() => {}}
            isDark={isDark}
          />
        </div>
      )}

      {/* Upload modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        folderId={selectedFolderId}
        onUploadComplete={fetchDrawings}
        isDark={isDark}
      />

      {/* Drawing Viewer */}
      {viewerVariant === 'field' && viewerMode === 'view' ? (
        <FieldDrawingViewer
          isOpen={showViewer}
          drawingUrl={viewerDrawing ? resolveDrawingFileUrl(viewerDrawing.file_url) : undefined}
          drawingTitle={viewerDrawing?.drawing_name}
          drawings={fieldViewerDrawings}
          onClose={() => { setShowViewer(false); setViewerDrawing(null); }}
          operationsPublishedDrawingsForWorkItemId={workItemId ?? null}
        />
      ) : (
        <DrawingViewer
          drawing={viewerDrawing}
          isOpen={showViewer}
          onClose={() => { setShowViewer(false); setViewerDrawing(null); }}
          drawings={filteredDrawings}
          onNavigate={(d) => setViewerDrawing(d)}
          mode={viewerMode}
          commentsWorkItemId={workItemId ?? null}
        />
      )}

      {/* Version Compare */}
      <DrawingVersionCompare
        drawing={compareDrawing!}
        versions={drawingVersions}
        isOpen={showVersionCompare}
        onClose={() => { setShowVersionCompare(false); setCompareDrawing(null); }}
      />

      {/* Batch Operations Bar */}
      <DrawingBatchOperations
        selectedDrawings={selectedDrawingsList}
        folders={folders}
        onMoveToFolder={handleBulkMoveToFolder}
        onBulkDelete={handleBulkDelete}
        onBulkStatusChange={handleBulkStatusChange}
        onBulkTagAdd={handleBulkTagAdd}
        onBulkDownload={handleBulkDownload}
        onBulkShare={embeddedInWorkItemDetail ? undefined : handleBulkShare}
        onBulkPrint={handleBulkPrint}
        onBulkLinkJob={handleBulkLinkJob}
        onCreateTransmittal={handleCreateTransmittal}
        onClearSelection={() => setSelectedDrawings(new Set())}
        isOpen={showBatchOperations && selectedDrawings.size > 0}
      />

      {/* Transmittal Modal */}
      <DrawingTransmittalModal
        drawings={selectedDrawingsList}
        isOpen={showTransmittal}
        onClose={() => setShowTransmittal(false)}
        onSend={handleSendTransmittal}
      />
    </div>
  );
}
